import Link from 'next/link'
import {useContext, useEffect, useState} from 'react'
import {getLevelHighScoreForPlayerCached} from 'src/util/db'
import {HStack, VStack} from './Stack'
import {LevelTileBase} from 'src/LevelTile'
import {Loader} from 'src/Loader'
import {Icon} from 'src/Icon'
import {
  isTodaysDailyTarget,
  isYesterdaysDailyTarget,
} from '@cssbattle/shared/utils'
import {CountDown} from 'src/CountDown'
import {useUser} from 'src/hooks/useUser'
import {Skeleton} from './Skeleton'
import {getChallengeText} from 'src/utils'
import {GlobalContext} from 'src/contexts'
import {ProBadge} from '@components/ProBadge'

const DailyTarget = ({target, showDateInside, lazy = true}) => {
  const user = useUser()
  const [isFetching, setIsFetching] = useState(false)
  const [data, setData] = useState()

  const [challengeText, setChallengeText] = useState('')
  const {streak} = useContext(GlobalContext)
  // set random challenge text when levelId changes
  useEffect(() => {
    if (!data || !target) return

    setChallengeText(
      getChallengeText({
        score: data.score,
        codeSize: data.codeSize,
        levelId: target.id,
        isDaily: true,
        date: target.formattedDate,
        streak: streak?.count,
      })
    )
  }, [target, data])

  useEffect(() => {
    if (target.id === 'dummy' || !user?.uid) return

    setIsFetching(true)
    getLevelHighScoreForPlayerCached({playerId: user.uid, levelId: target.id})
      .then((data) => {
        setData(data)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [target?.id, user?.uid])

  const isTodaysTarget = isTodaysDailyTarget(target)
  const isYesterdaysTarget = isYesterdaysDailyTarget(target)

  return (
    <VStack gap="1rem">
      {!showDateInside && (
        <div className={`pill ${data ? 'pill--success' : ''} `}>
          {data ? <Icon name="tick-round" size={'1em'} color="white" /> : null}{' '}
          {target.formattedDate}
          {isTodaysTarget ? ' (Today) ' : ''}
        </div>
      )}
      <div className={isTodaysTarget ? 'target-today' : ''}>
        <LevelTileBase
          target={target}
          data={data}
          showTags={showDateInside}
          lazy={lazy}
          useCdnUrl={true}
        >
          <div style={{padding: '1rem 1rem 0.5rem'}}>
            {target.id !== 'dummy' ? (
              <HStack justify="space-between">
                <div className="daily-target-text">
                  <VStack gap="0" align="flex-start">
                    <p className="daily-target-text__title">Your score</p>
                    <div className="daily-target-text__score">
                      {isFetching ? (
                        <Skeleton width="6ch" />
                      ) : data ? (
                        `${data.score || data.overall.score} {${
                          data.codeSize || data.overall.codeSize
                        }}`
                      ) : (
                        'Not played'
                      )}
                    </div>
                  </VStack>
                </div>

                <HStack extend={true} gap="0.5rem">
                  {isTodaysTarget && data ? (
                    <a
                      href={`https://twitter.com/share?url=https://cssbattle.dev/play/${target?.id}&text=${challengeText}&hashtags=CSSBattleChallenge&via=css_battle&related=css_battle`}
                      target="_blank"
                      rel="external noopener noreferrer"
                      className="button button--circular"
                    >
                      <Icon
                        name="twitter"
                        color="var(--clr-text-light)"
                        marginRight="0"
                        size="20"
                      />
                    </a>
                  ) : null}

                  {isYesterdaysTarget && !user?.isPro ? (
                    <Link
                      href={{
                        pathname: `/play/${target.id}`,
                        query: {openTopSolutions: true},
                      }}
                    >
                      <a
                        className={
                          isTodaysDailyTarget(target)
                            ? 'button--primary button button--circular'
                            : 'button button--circular'
                        }
                      >
                        <Icon
                          name="open-book"
                          color={
                            isTodaysDailyTarget(target)
                              ? 'var(--clr-btn-text)'
                              : 'var(--clr-text-light)'
                          }
                          marginRight="0"
                          size="20"
                        />
                      </a>
                    </Link>
                  ) : isTodaysTarget || user?.isPro ? (
                    <Link href={`/play/${target.id}`}>
                      <a
                        className={
                          isTodaysDailyTarget(target)
                            ? 'button--primary button button--circular'
                            : 'button button--circular'
                        }
                      >
                        <Icon
                          name="play"
                          color={
                            isTodaysDailyTarget(target)
                              ? 'var(--clr-btn-text)'
                              : 'var(--clr-text-light)'
                          }
                          marginRight="0"
                          size="20"
                        />
                      </a>
                    </Link>
                  ) : (
                    <ProBadge />
                  )}
                </HStack>
              </HStack>
            ) : (
              <div className="daily-target-text">
                <p
                  className="daily-target-text__title"
                  style={{textAlign: 'center'}}
                >
                  Unlocks in
                </p>
                <div style={{marginTop: '0.5rem'}}>
                  <CountDown
                    showEmoji={false}
                    suffix=""
                    date={target.date * 1000}
                    size="0.1"
                    showUnits={false}
                  />
                </div>
              </div>
            )}
          </div>
        </LevelTileBase>
      </div>
    </VStack>
  )
}

export {DailyTarget}
